import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Printer, Building2 } from "lucide-react";

/**
 * Printable customer statement — all invoices & payments for one party
 * with running balance, ALWAYS firm-scoped so books stay separate across
 * Wellpack's multiple selling firms. If the URL is missing `?firm=<id>`
 * we render a firm picker instead of the merged (all-firms) ledger.
 */
export default function CustomerStatement() {
  const { cid } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [firms, setFirms] = useState([]);
  const firm = useMemo(() =>
    new URLSearchParams(window.location.search).get("firm") || "",
  []);

  useEffect(() => {
    api.get("/billing-companies").then((r) => setFirms(r.data || [])).catch(() => setFirms([]));
  }, []);

  useEffect(() => {
    if (!firm) return;                             // wait for user to pick a firm
    const q = `?company_id=${encodeURIComponent(firm)}`;
    api.get(`/customers/${cid}/statement${q}`).then((r) => setData(r.data)).catch(() => setErr("Failed to load statement"));
  }, [cid, firm]);

  const pickFirm = (id) => {
    const url = new URL(window.location.href);
    url.searchParams.set("firm", id);
    window.location.replace(url.toString());
  };

  if (!firm) {
    return (
      <div className="min-h-screen bg-zinc-100 p-8 flex items-center justify-center" data-testid="statement-firm-picker">
        <div className="w-full max-w-lg bg-white border border-zinc-300 p-6">
          <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600 mb-1">Pick a Firm</div>
          <h1 className="text-xl font-bold text-zinc-950">Which firm's statement do you want?</h1>
          <p className="text-xs text-zinc-600 mt-2 font-sans">
            Every registered firm keeps its own books — invoices, receipts, notes and opening balances
            never mix. Choose the firm below to open the statement for that firm only.
          </p>
          <div className="mt-4 space-y-2">
            {firms.length === 0 && (
              <div className="text-xs text-zinc-500 font-mono">Loading firms…</div>
            )}
            {firms.map((f) => (
              <button key={f.id} onClick={() => pickFirm(f.id)}
                data-testid={`pick-firm-${f.id}`}
                className="w-full text-left border border-zinc-300 hover:border-orange-500 hover:bg-orange-50 px-4 py-3 flex items-center gap-3 group">
                <Building2 size={16} className="text-zinc-500 group-hover:text-orange-600" />
                <div>
                  <div className="text-sm font-semibold text-zinc-900">{f.name}</div>
                  {f.gst && <div className="text-[10px] font-mono text-zinc-500">GST · {f.gst}</div>}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (err) return <div className="p-10 font-mono text-xs text-red-700">{err}</div>;
  if (!data) return <div className="p-10 font-mono text-xs text-zinc-500">Loading statement…</div>;
  const firmObj = firms.find((f) => f.id === firm) || {};

  const { customer, transactions, totals, aging = {}, as_of } = data;
  const fmt = (n) => (n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const buckets = [
    { k: "current", label: "Not Due", tone: "zinc" },
    { k: "0-15", label: "0–15", tone: "yellow" },
    { k: "16-30", label: "16–30", tone: "orange" },
    { k: "31-45", label: "31–45", tone: "red" },
    { k: "46-60", label: "46–60", tone: "red" },
    { k: "60+", label: "60+", tone: "red" },
  ];

  return (
    <div className="min-h-screen bg-zinc-100 print:bg-white p-4 print:p-0 flex justify-center" data-testid="customer-statement">
      <div className="w-full max-w-4xl bg-white p-8 print:p-4 border border-zinc-300 print:border-0 shadow-sm">
        <div className="flex items-center justify-between border-b-2 border-zinc-900 pb-4 mb-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Customer Statement</div>
            <h1 className="text-2xl font-bold tracking-tight text-zinc-950 mt-1">{customer.name}</h1>
            {customer.company && <div className="text-sm text-zinc-700">{customer.company}</div>}
            <div className="text-[11px] font-mono text-zinc-500 mt-1">
              {customer.phone && <>Phone: {customer.phone} · </>}{customer.gst && <>GST: {customer.gst}</>}
            </div>
            <div className="mt-2 inline-flex items-center gap-2 bg-orange-50 border border-orange-500 text-orange-800 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest"
                 data-testid="stmt-firm-badge">
              <Building2 size={11} />
              <span>Firm · {firmObj.name || firm}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">As of</div>
            <div className="text-lg font-semibold">{as_of}</div>
            <button onClick={() => window.print()} data-testid="print-statement"
              className="mt-2 inline-flex items-center gap-2 border border-zinc-800 text-zinc-800 hover:bg-zinc-900 hover:text-white px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider print:hidden">
              <Printer size={12} /> Print / Save PDF
            </button>
          </div>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-5 gap-2 mb-3">
          <div className="border border-zinc-300 bg-zinc-50 p-2 text-center">
            <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">Invoiced</div>
            <div className="text-lg font-semibold text-zinc-900">₹ {fmt(totals.invoiced)}</div>
          </div>
          <div className="border border-emerald-300 bg-emerald-50 p-2 text-center">
            <div className="text-[9px] font-mono uppercase tracking-widest text-emerald-700">Paid</div>
            <div className="text-lg font-semibold text-emerald-900">₹ {fmt(totals.paid)}</div>
          </div>
          <div className="border-2 border-orange-500 bg-orange-50 p-2 text-center">
            <div className="text-[9px] font-mono uppercase tracking-widest text-orange-700">Balance Due</div>
            <div className="text-xl font-bold text-orange-900">₹ {fmt(totals.balance)}</div>
          </div>
          <div className={`border-2 p-2 text-center ${totals.overdue > 0 ? "border-red-600 bg-red-50" : "border-zinc-300 bg-zinc-50"}`} data-testid="stmt-overdue-tile">
            <div className={`text-[9px] font-mono uppercase tracking-widest ${totals.overdue > 0 ? "text-red-700" : "text-zinc-500"}`}>Total Overdue</div>
            <div className={`text-xl font-bold ${totals.overdue > 0 ? "text-red-800" : "text-zinc-700"}`}>₹ {fmt(totals.overdue)}</div>
            {totals.oldest_overdue_days > 0 && (
              <div className="text-[9px] font-mono text-red-700">oldest {totals.oldest_overdue_days}d overdue</div>
            )}
          </div>
          <div className={`border-2 p-2 text-center ${(totals.advance_balance || 0) > 0 ? "border-blue-500 bg-blue-50" : "border-zinc-300 bg-zinc-50"}`} data-testid="stmt-advance-tile">
            <div className={`text-[9px] font-mono uppercase tracking-widest ${(totals.advance_balance || 0) > 0 ? "text-blue-700" : "text-zinc-500"}`}>Advance</div>
            <div className={`text-xl font-bold ${(totals.advance_balance || 0) > 0 ? "text-blue-800" : "text-zinc-700"}`}>₹ {fmt(totals.advance_balance || 0)}</div>
            {(totals.advance_balance || 0) > 0 && (
              <div className="text-[9px] font-mono text-blue-700">auto-applies next invoice</div>
            )}
          </div>
        </div>

        {/* Aging breakdown */}
        {totals.balance > 0.01 && (
          <div className="grid grid-cols-6 gap-1 mb-4" data-testid="stmt-aging">
            {buckets.map((b) => {
              const amt = aging[b.k] || 0;
              const tone = amt > 0 ? {
                zinc: "border-zinc-400 bg-zinc-100 text-zinc-800",
                yellow: "border-yellow-400 bg-yellow-50 text-yellow-800",
                orange: "border-orange-500 bg-orange-50 text-orange-800",
                red: "border-red-500 bg-red-50 text-red-800",
              }[b.tone] : "border-zinc-200 bg-white text-zinc-400";
              return (
                <div key={b.k} className={`border ${tone} px-2 py-1 text-center`}>
                  <div className="text-[9px] font-mono uppercase tracking-widest">{b.label}</div>
                  <div className="text-xs font-semibold">₹ {fmt(amt)}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Ledger */}
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-zinc-900 text-white font-mono uppercase tracking-wider text-[9px]">
              <th className="text-left px-2 py-1.5">Date</th>
              <th className="text-left px-2 py-1.5">Ref</th>
              <th className="text-left px-2 py-1.5">Particulars</th>
              <th className="text-left px-2 py-1.5">Due Date</th>
              <th className="text-right px-2 py-1.5">Overdue</th>
              <th className="text-right px-2 py-1.5">Debit ₹</th>
              <th className="text-right px-2 py-1.5">Credit ₹</th>
              <th className="text-right px-2 py-1.5">Balance ₹</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {transactions.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-8 text-zinc-500 font-sans">No transactions.</td></tr>
            ) : transactions.map((t, i) => {
              const isOverdue = t.type === "invoice" && t.days_overdue > 0 && (t.balance_due || 0) > 0.01;
              const isAdvRecv = t.type === "advance_received";
              const isAdvApp = t.type === "advance_applied";
              const isOpening = t.type === "opening";
              const isNoteCr = t.type === "note_credit";
              const isNoteDr = t.type === "note_debit";
              const rowBg = isOpening ? "bg-amber-50/60"
                : (isAdvRecv ? "bg-blue-50/60"
                : (isAdvApp ? "bg-blue-50/30"
                : (isNoteCr ? "bg-amber-50/70"
                : (isNoteDr ? "bg-purple-50/70"
                : (t.type === "payment" ? "bg-emerald-50/40"
                : (isOverdue ? "bg-red-50/60" : ""))))));
              return (
                <tr key={i} className={`border-b border-zinc-200 ${rowBg}`} data-testid={`stmt-row-${t.type}-${i}`}>
                  <td className="px-2 py-1.5">{t.date}</td>
                  <td className="px-2 py-1.5 font-semibold">
                    {t.ref}
                    {isOpening && <span className="ml-1 text-[8px] uppercase bg-amber-200 text-amber-900 px-1">carry-fwd</span>}
                    {isAdvRecv && <span className="ml-1 text-[8px] uppercase bg-blue-200 text-blue-900 px-1">advance</span>}
                    {isAdvApp && <span className="ml-1 text-[8px] uppercase bg-blue-100 text-blue-800 px-1">applied</span>}
                    {isNoteCr && <span className="ml-1 text-[8px] uppercase bg-amber-300 text-amber-900 px-1">CR note</span>}
                    {isNoteDr && <span className="ml-1 text-[8px] uppercase bg-purple-300 text-purple-900 px-1">DR note</span>}
                  </td>
                  <td className={`px-2 py-1.5 font-sans ${isNoteCr ? "text-amber-800" : (isNoteDr ? "text-purple-800" : "")}`}>{t.particulars}</td>
                  <td className="px-2 py-1.5">{t.type === "invoice" ? (t.due_date || "—") : ""}</td>
                  <td className={`px-2 py-1.5 text-right ${isOverdue ? "text-red-700 font-semibold" : "text-zinc-500"}`}>
                    {t.type === "invoice" ? (t.days_overdue > 0 ? `${t.days_overdue}d` : (t.balance_due > 0.01 ? "—" : "✓")) : ""}
                  </td>
                  <td className={`px-2 py-1.5 text-right ${isNoteDr ? "text-purple-800 font-semibold" : ""}`}>{t.debit > 0 ? fmt(t.debit) : ""}</td>
                  <td className={`px-2 py-1.5 text-right ${isAdvRecv ? "text-blue-800" : (isNoteCr ? "text-amber-800 font-semibold" : "text-emerald-700")}`}>{t.credit > 0 ? fmt(t.credit) : ""}</td>
                  <td className="px-2 py-1.5 text-right font-semibold">{fmt(t.balance)}</td>
                </tr>
              );
            })}
            <tr className="bg-zinc-100 font-bold border-t-2 border-zinc-900">
              <td colSpan={5} className="px-2 py-2 text-right">TOTAL</td>
              <td className="px-2 py-2 text-right">{fmt(totals.invoiced)}</td>
              <td className="px-2 py-2 text-right text-emerald-800">{fmt(totals.paid)}</td>
              <td className="px-2 py-2 text-right text-orange-800">{fmt(totals.balance)}</td>
            </tr>
          </tbody>
        </table>

        <div className="mt-6 text-[10px] font-mono text-zinc-500 border-t border-zinc-200 pt-2">
          Statement generated on {as_of}. Please verify balances and reach out for any discrepancies.
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          html, body { background: white !important; }
        }
      `}</style>
    </div>
  );
}
